import {
    useCallback,
    useEffect,
    useLayoutEffect,
    useMemo,
    type CSSProperties,
} from "react";
import {
    useDefaultLayout,
    type Layout,
    type LayoutChangedMeta,
} from "react-resizable-panels";
import {
    ResizableHandle,
    ResizablePanel,
    ResizablePanelGroup,
} from "@design-system/ResizablePanels/ResizablePanels";
import { TaskType } from "@common/types/task";
import { AnalyticsEvent } from "@common/constants/analytics-events";
import { useTaskDetail } from "@api/task.api";
import { trackEvent } from "@lib/analytics";
import { TaskDetailPanel } from "./TaskDetail/TaskDetailPanel";
import { TaskBodyPanel } from "./TaskBody/TaskBodyPanel";
import { TaskBodyHeader } from "./TaskBody/TaskBodyHeader";
import { useTasksContext } from "./tasks.context";
import type { TaskBoardTask } from "./types";
import { showTaskSection } from "./showTaskSection";
import { useTaskPaneMotion } from "./useTaskPaneMotion";
import { TaskViewFallback } from "./TaskViewFallback";
import { SpreadsheetRefProvider } from "./SpreadsheetRefContext";
import { TransactionRefProvider } from "./TransactionRefContext";
import { CounterpartyRefProvider } from "./CounterpartyRefContext";
import { InvoiceRefProvider } from "./InvoiceRefContext";
import { DocumentRefProvider } from "./DocumentRefContext";
import { TaskChatDndProvider } from "./chatDrag/TaskChatDndProvider";
import styles from "./SelectedTaskContainer.module.scss";

const PANE_ID = "task-detail-pane";
const PANE_STYLE: CSSProperties = { overflow: "hidden" };
// Below this, the three equal-width tabs leave "Dependencies" no side padding.
const PANE_MIN_SIZE = "340px";

interface SelectedTaskContainerProps {
    tasks: TaskBoardTask[];
    navigationTaskIds: string[];
    /** Task whose chat a deep link asked for; opened once that task is on screen. */
    chatTaskId?: string;
    onChatShown?: () => void;
}

/** The open task: its top bar over the resizable body and right pane. */
export const SelectedTaskContainer: React.FC<SelectedTaskContainerProps> = ({
    tasks,
    navigationTaskIds,
    chatTaskId,
    onChatShown,
}) => {
    const { defaultLayout, onLayoutChanged } = useDefaultLayout({
        id: "task-view-panel-group",
        storage: localStorage,
        onlySaveAfterUserInteractions: true,
    });
    const {
        selectedTaskId,
        goToTask,
        closeTask,
        taskDetailPanelRef,
        setTaskDetailPanelCollapsed,
        setTaskDetailPanelContent,
        requestTaskDetailSection,
    } = useTasksContext();
    const { groupElementRef, paneContentRef, animateToggle } =
        useTaskPaneMotion();

    const { data: detailTask, isLoading: isDetailLoading } = useTaskDetail(
        selectedTaskId ?? "",
    );

    const selectedTask = useMemo(
        () => tasks.find((task) => task.id === selectedTaskId) ?? null,
        [selectedTaskId, tasks],
    );

    const currentTask = detailTask ?? selectedTask;
    const currentTaskId = currentTask?.id;
    const selectedTaskIndex = navigationTaskIds.indexOf(selectedTaskId ?? "");
    const hasTaskInList = selectedTaskIndex !== -1;

    const goToTaskAt = useCallback(
        (direction: "previous" | "next") => {
            const index =
                selectedTaskIndex + (direction === "previous" ? -1 : 1);
            const taskId = navigationTaskIds[index];
            if (!hasTaskInList || taskId === undefined) {
                return;
            }
            void trackEvent(AnalyticsEvent.TASK_NAVIGATION_CLICKED, {
                direction,
                taskId: selectedTaskId,
                taskType: selectedTask?.type,
                fromIndex: selectedTaskIndex + 1,
                totalTasks: navigationTaskIds.length,
            });
            goToTask({
                taskId,
                workspaceId: tasks.find((task) => task.id === taskId)
                    ?.workspaceId,
            });
        },
        [
            goToTask,
            hasTaskInList,
            navigationTaskIds,
            selectedTask?.type,
            selectedTaskId,
            selectedTaskIndex,
            tasks,
        ],
    );

    useLayoutEffect(() => {
        if (!currentTaskId) {
            return;
        }
        taskDetailPanelRef.current?.expand();
    }, [currentTaskId, taskDetailPanelRef]);

    const detailTaskId = detailTask?.id;
    useEffect(() => {
        if (!chatTaskId || detailTaskId !== chatTaskId) {
            return;
        }
        showTaskSection({
            taskDetailPanelRef,
            setTaskDetailPanelContent,
            requestTaskDetailSection,
            section: "chat",
            taskId: chatTaskId,
        });
        onChatShown?.();
    }, [
        chatTaskId,
        detailTaskId,
        onChatShown,
        setTaskDetailPanelContent,
        requestTaskDetailSection,
        taskDetailPanelRef,
    ]);

    const togglePane = useCallback(() => {
        const pane = taskDetailPanelRef.current;
        if (pane?.isCollapsed()) {
            pane.expand();
        } else {
            pane?.collapse();
        }
    }, [taskDetailPanelRef]);

    const saveExpandedLayout = useCallback(
        (layout: Layout, meta: LayoutChangedMeta) => {
            if ((layout[PANE_ID] ?? 0) > 0) {
                onLayoutChanged(layout, meta);
            }
        },
        [onLayoutChanged],
    );

    if (!currentTask) {
        return (
            <TaskViewFallback isLoading={isDetailLoading} close={closeTask} />
        );
    }

    const content = (
        <div className={styles.taskContainer}>
            <TaskBodyHeader
                selectedTask={currentTask}
                close={closeTask}
                hasTaskInList={hasTaskInList}
                hasUnreadMessages={(selectedTask?.unreadCount ?? 0) > 0}
                onTogglePane={(event) => animateToggle(event, togglePane)}
                currentIndex={hasTaskInList ? selectedTaskIndex + 1 : 0}
                totalTasks={navigationTaskIds.length}
                canGoToPreviousTask={hasTaskInList && selectedTaskIndex > 0}
                canGoToNextTask={
                    hasTaskInList &&
                    selectedTaskIndex < navigationTaskIds.length - 1
                }
                goToPreviousTask={() => goToTaskAt("previous")}
                goToNextTask={() => goToTaskAt("next")}
            />
            <ResizablePanelGroup
                orientation="horizontal"
                defaultLayout={defaultLayout}
                onLayoutChanged={saveExpandedLayout}
                elementRef={groupElementRef}
                className={styles.panelGroup}
            >
                <ResizablePanel id="task-body">
                    <TaskBodyPanel selectedTask={currentTask} />
                </ResizablePanel>
                <ResizableHandle />
                <ResizablePanel
                    id={PANE_ID}
                    defaultSize={PANE_MIN_SIZE}
                    minSize={PANE_MIN_SIZE}
                    maxSize="40%"
                    collapsible
                    groupResizeBehavior="preserve-pixel-size"
                    panelRef={taskDetailPanelRef}
                    style={PANE_STYLE}
                    onResize={() =>
                        setTaskDetailPanelCollapsed(
                            taskDetailPanelRef.current?.isCollapsed() ?? false,
                        )
                    }
                >
                    <div ref={paneContentRef} className={styles.paneContent}>
                        <TaskDetailPanel
                            selectedTask={currentTask}
                            key={currentTask.id}
                        />
                    </div>
                </ResizablePanel>
            </ResizablePanelGroup>
        </div>
    );

    // Tasks that embed a SpreadsheetGrid and support "Ask about cells" need the
    // SpreadsheetRefProvider so cell selections can be wired into the chat
    // composer. Add every task type that uses the spreadsheet cell-ref feature.
    const isSpreadsheetRefTask =
        currentTask.type === TaskType.PROFIT_LOSS_ACTIVATION ||
        currentTask.type === TaskType.PROFIT_LOSS ||
        currentTask.type === TaskType.BALANCE_SHEET;
    const taskId = selectedTaskId ?? "";

    return (
        <TransactionRefProvider taskId={taskId}>
            <CounterpartyRefProvider taskId={taskId}>
                <InvoiceRefProvider taskId={taskId}>
                    <DocumentRefProvider taskId={taskId}>
                        <SpreadsheetRefProvider
                            taskId={isSpreadsheetRefTask ? taskId : null}
                        >
                            <TaskChatDndProvider>{content}</TaskChatDndProvider>
                        </SpreadsheetRefProvider>
                    </DocumentRefProvider>
                </InvoiceRefProvider>
            </CounterpartyRefProvider>
        </TransactionRefProvider>
    );
};
